// First: no module may parse with Zod before it is told not to compile (zod-config.ts).
import './zod-config';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
// The page's own fonts, served from the site (DIRECTION D12): Inter for
// everything, Bricolage Grotesque for display (VISUAL_IDENTITY.md). A look's
// others load with the look (`data/look.tsx`).
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/bricolage-grotesque/800.css';
import './styles/app-shell.css';
import './styles/tokens.css';
import './styles/kit.css';
import './styles/ui.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Notifications } from '@mantine/notifications';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { DetailProvider } from './data/detail';
import { LookProvider } from './data/look';
import { SessionProvider } from './data/session';
import { AccountPrefsProvider } from './data/accountPrefs';
import { DemoProvider } from './data/demo';
import { LeaguesProvider } from './data/LeaguesProvider';
import { SimProvider } from './data/SimProvider';
import { registerWorker } from './data/serviceWorker';
import { captureInstallPrompt } from './install/prompt';
import { migrateLegacyKeys } from './data/storage';
import { ThemeProvider } from './theme/ThemeProvider';
import { LookDefs } from './kit/art';
import { installErrorReporting } from './data/errorReports';
import { RouteBoundary } from './shell/ScreenBoundary';
import { SheetHistory } from './kit/backCloses';

// The first-paint script in index.html has already done this, which is what
// keeps the theme from flashing. Running it again costs one pass over a handful
// of keys and converges to nothing — it is here so the app is still correct if
// that script is ever removed, rather than quietly resetting everybody.
migrateLegacyKeys(localStorage);

// What breaks in this browser is told to the site (scenario O7): listening
// before the first render, so a failure while drawing it is heard too.
installErrorReporting();

// A PRESSED STATE ON AN IPHONE (UX-37). Safari on iOS draws `:active` only
// while something on the page listens for touches, so without this every
// control's pressed look — the one a finger gets in place of the browser's
// tap flash, which `app-shell.css` turns off — never shows there. It does
// nothing else, and passive so it never holds a scroll up.
document.addEventListener('touchstart', () => {}, { passive: true });

// The app shell and push (INSTALL.md §4.2): after the first screen has loaded.
registerWorker();
// The browser's install dialog, held for our button (INSTALL.md §4.3).
captureInstallPrompt();

// The router sits above the data provider: the active league is read from
// the URL (SITE_HOME.md routes). The session decides whether the root is
// the front door or the hub (ACCOUNTS.md).
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* The look outside the theme: a look may lay its own ground, which the
        palette resolver needs to know (P7). */}
    <LookProvider>
      <ThemeProvider>
        <LookDefs />
        <DetailProvider>
          <SessionProvider>
            <AccountPrefsProvider>
              <LeaguesProvider>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <DemoProvider>
                    <SimProvider>
                      <Notifications position="top-center" />
                      {/* Back closes the sheet on top first (B-308). */}
                      <SheetHistory />
                      {/* A screen drawn outside the shell — the front door, a
                        join — that throws still leaves a way on (B-270). */}
                      <RouteBoundary>
                        <App />
                      </RouteBoundary>
                    </SimProvider>
                  </DemoProvider>
                </BrowserRouter>
              </LeaguesProvider>
            </AccountPrefsProvider>
          </SessionProvider>
        </DetailProvider>
      </ThemeProvider>
    </LookProvider>
  </React.StrictMode>,
);
